import { Formik, useField } from 'formik';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FilterCompletionInput, FilterCompletionOutput, FilterSection } from '../../../../../../../../typings/AICompletion.interface';
import { fieldName } from '../../../../../utils';
import AIPromptDropdown from '../../../../common/AIPromptDropdown';
import Icon from '../../../../common/base/Icon';
import Keybinds from '../../../../common/base/Keybinds';
import Link from '../../../../common/base/Link';
import Text from '../../../../common/base/Text';
import EditorInput from '../../../../common/Form/Fields/EditorInput';
import TextAreaInput from '../../../../common/Form/Fields/TextAreaInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import useAIFilter from './useAIFilter';

// Filters have no schema, so the JSON suggestions (empty object / array snippets) are only noise.
// Filters are short, so sticky scroll only hides lines.
const FILTER_EDITOR_OPTIONS = {
  stickyScroll: {
    enabled: false
  },
  quickSuggestions: false,
  suggestOnTriggerCharacters: false,
  wordBasedSuggestions: false,
  snippetSuggestions: 'none' as const
};

// Text that isn't JSON and starts like a sentence is treated as a filter description.
export const isFilterDescription = (value: unknown): value is string => {
  if (typeof value !== 'string' || !/^\s*[a-z]/i.test(value)) {
    return false;
  }
  try {
    JSON.parse(value);
    return false;
  } catch {
    return true;
  }
};
interface FilterEditorInputProps {
  // Form field of the filter rule, e.g. `rules[0]`.
  rule_name: string;
  section: FilterSection;
  height?: string;
  format_on_blur?: boolean;
  // Only editable by team members when set, like the rest of the connection rules.
  permission?: boolean;
  // Errors are displayed by the parent, e.g. below the rule card.
  hide_errors?: boolean;
  getSampleInput: () => Promise<FilterCompletionInput['input']>;
  onGenerated?: (filters: NonNullable<FilterCompletionOutput['filters']>) => void;
  onBlur?: () => void;
  // Renders the AI action in this element (e.g. a header) with a prompt dropdown, instead of
  // the editor footer with an inline prompt.
  action_container?: HTMLElement | null;
}

// Filter JSON editor with AI editing: a plain-language description typed in the editor is
// turned into a filter, and the current filter can be edited from a prompt.
const FilterEditorInput: React.FC<FilterEditorInputProps> = ({
  rule_name,
  section,
  height,
  format_on_blur,
  permission,
  hide_errors,
  getSampleInput,
  onGenerated,
  onBlur,
  action_container
}) => {
  const name = fieldName(section, rule_name);
  const [{
    value
  }] = useField(name);
  const {
    generateFilter,
    generation
  } = useAIFilter(rule_name);
  const [is_generating, setIsGenerating] = useState(false);
  const [is_prompt_open, setPromptOpen] = useState(false);
  const is_description = isFilterDescription(value);
  const generate = async (prompt: string) => {
    setIsGenerating(true);
    try {
      const input = await getSampleInput().catch(() => ({}));
      const filters = await generateFilter(prompt, input);
      filters && onGenerated?.(filters);
      return !!filters;
    } finally {
      setIsGenerating(false);
    }
  };
  const onGenerateFromDescription = () => {
    if (!is_generating && isFilterDescription(value)) {
      generate(value.trim());
    }
  };

  // The editor binds ⌘↵ once on mount, so route it to the latest handler.
  const on_generate_ref = useRef(onGenerateFromDescription);
  on_generate_ref.current = onGenerateFromDescription;
  const renderAction = (label: string, onClick?: () => void, keybind?: boolean) => is_generating ? <Text as="span" size="s" shimmering flex={{
    align: 'center'
  }}>
        <Icon small left icon="auto_awesome" />
        Generating filter
      </Text> :
  // Keybinds include their own spacing, no extra gap.
  <Div flex={{
    align: 'center'
  }}>
        {/* The icon is outside the link so the link hover underline only applies to the label */}
        <Icon small left pointer primary icon="auto_awesome" onClick={onClick} />
        <Link as="button" type="button" small primary onClick={onClick}>
          {label}
        </Link>
        {keybind && <Keybinds keys={['CMD', '⏎']} />}
      </Div>;
  const Editor = permission ? EditorInput.Permission : EditorInput;
  const prompt_input =
  // Rendered without a <form> element since the filter is part of another form.
  <Formik initialValues={{
    prompt: ''
  }} onSubmit={async ({
    prompt: prompt_0
  }, {
    resetForm
  }) => {
    if (await generate(prompt_0.trim())) {
      resetForm();
      setPromptOpen(false);
    }
  }}>
      {({
      submitForm,
      values
    }) => <Div onKeyDown={e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        // Submit the prompt instead of the form the filter is part of.
        e.preventDefault();
        values.prompt.trim() && !is_generating && submitForm();
      } else if (e.key === 'Escape') {
        setPromptOpen(false);
      }
    }}>
          <TextAreaInput name="prompt" focus rows={2} m={0} maxlength={4000} disabled={is_generating} placeholder="Describe the change, e.g. also skip test events, then press enter" />
        </Div>}
    </Formik>;
  const action = is_description ? renderAction('Generate filter', onGenerateFromDescription, true) : action_container ? <AIPromptDropdown label="Edit with AI" placeholder="Describe the change, e.g. also skip test events" submit_label="Update filter" renderToggle={(opened, toggle) => renderAction('Edit with AI', () => toggle(!opened))} onSubmit={generate} /> : renderAction('Edit with AI', () => setPromptOpen(!is_prompt_open));
  const editor = <Editor key={`${section}-${generation}`} flex={{
    direction: 'column',
    grow: true
  }} format_on_blur={format_on_blur} display="minimal" height={height} name={name} disabled={is_generating} placeholder={'Describe your event filter...\ni.e. Only paid orders over $100'}
  // A description isn't meant to be valid JSON, generating replaces it.
  hide_error={hide_errors || is_description} cmdEnter={() => on_generate_ref.current()} options={FILTER_EDITOR_OPTIONS} onBlur={onBlur} footer_action={action_container === undefined ? action : undefined} footer_content={action_container === undefined && is_prompt_open && !is_description && prompt_input} />;
  if (action_container === undefined) {
    return editor;
  }
  return <>
      {editor}
      {action_container && createPortal(action, action_container)}
    </>;
};
export default FilterEditorInput;